// Boost Blenz Admin — Books (Overview → its own tab). PAID ebook sales only —
// copies someone actually bought. Free copies are excluded on purpose: the
// "Living Parasite Free" ebook is auto-added at $0 to physical detox orders, and
// promos can zero a price; those are giveaways, not sales. Reads
// /api/ebooks/series: `periods` = paid revenue per period (Shopify total_sales,
// which already ignores $0 lines), over ONE fixed window for every grain so
// weekly/monthly/yearly totals agree; `breakdown` = the current period's exact
// paid unit counts (order-line scan); `allRevenue`/`allByBook` = all-time totals.
import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon, Card, Title } from "../atoms";
import { useCachedJson } from "../datacache";

type Grain = "week" | "month" | "year";
type Period = { key: string; label: string; partial: boolean; books: Record<string, { copies: number; revenue: number }>; copies: number; revenue: number };
type BreakRow = { sku: string; name: string; bought: number; revenue: number };
type Breakdown = { books: BreakRow[]; bought: number; revenue: number };

const money = (n: number, dp = 2) => "$" + (Math.round(n * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
const int = (n: number) => Math.round(n).toLocaleString("en-US");
const HUES = ["#a78bfa", "#5fd39a", "#f8925c", "#6aa9e0", "#f5c451", "#ee5a6a", "#b2aac6"];

function Spark({ vals, color, w = 104, h = 28 }: { vals: number[]; color: string; w?: number; h?: number }) {
  if (vals.length < 2 || Math.max(...vals) <= 0) return <div style={{ width: w, height: h }} />;
  const max = Math.max(1, ...vals);
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`);
  const last = vals[vals.length - 1];
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }}>
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
      <circle cx={(vals.length - 1) * step} cy={h - (last / max) * (h - 4) - 2} r={2.6} fill={color} />
    </svg>
  );
}

export function BooksView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [grain, setGrain] = React.useState<Grain>("month");
  const { data, loading } = useCachedJson<any>(`/api/ebooks/series?grain=${grain}`);

  const periods: Period[] = data?.periods || [];
  const names: Record<string, string> = data?.names || {};
  const breakdown: Breakdown = data?.breakdown || { books: [], bought: 0, revenue: 0 };
  const allByBook: Record<string, { copies: number; revenue: number }> = data?.allByBook || {};
  const allRevenue: number = data?.allRevenue || 0;
  const allCopies: number = data?.allCopies || 0;
  const wrongful: { order: string; date: string; amount: number }[] = data?.wrongful || [];
  const wrongfulTotal = wrongful.reduce((s, w) => s + w.amount, 0);
  const shown = grain === "year" ? periods : periods.slice(-12);
  const perWord = grain === "week" ? "week" : grain === "month" ? "month" : "year";

  // One row per title that has real sales — this-period paid count/revenue + all-time
  // copies/revenue + a revenue sparkline, all from the same ledger. Best-seller first.
  const skus = Array.from(new Set([...Object.keys(allByBook), ...breakdown.books.map((b) => b.sku)]));
  const books = skus.map((sku) => {
    const br = breakdown.books.find((b) => b.sku === sku);
    return {
      sku,
      name: names[sku] || br?.name || sku,
      bought: br?.bought || 0,
      periodRev: br?.revenue || 0,
      allBought: allByBook[sku]?.copies || 0,
      allRev: allByBook[sku]?.revenue || 0,
      revSeries: shown.map((p) => p.books[sku]?.revenue || 0),
    };
  }).filter((r) => r.allRev > 0 || r.bought > 0)
    .sort((a, b) => b.periodRev - a.periodRev || b.allRev - a.allRev);

  const titlesSelling = books.length;
  const revMax = Math.max(1, ...shown.map((p) => p.revenue));
  const empty = !loading && allRevenue === 0 && breakdown.bought === 0;

  const Toggle = ({ g, lbl }: { g: Grain; lbl: string }) => (
    <span onClick={() => setGrain(g)} style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.08em", fontSize: 12, padding: "9px 20px", borderRadius: 999, cursor: "pointer", textTransform: "uppercase", background: grain === g ? "#f4f1ff" : "transparent", color: grain === g ? "#1a1330" : t.muted }}>{lbl}</span>
  );
  const Tile = ({ big, cap, color, sub }: any) => (
    <Card pad={20}>
      <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 30, color: color || t.fg, lineHeight: 1 }}>{big}</div>
      <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 7 }}>{cap}</div>
      {sub != null && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 2 }}>{sub}</div>}
    </Card>
  );
  // Fixed last track (not `auto`): a CSS grid sizes `auto` from each grid's own
  // content, so the header row (word "Trend") and the data rows (104px sparkline)
  // would compute different widths and the headers wouldn't sit over their columns.
  // A fixed width makes the header and row templates identical, so they align.
  const cols = isMobile ? "1fr 116px" : "2fr 0.9fr 0.9fr 116px";

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <Title size={isMobile ? 24 : 32}>Books</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>Paid ebook sales — copies someone actually bought. Free giveaways ($0) are excluded. Digital goods, so revenue ≈ profit.</div>
        </div>
        <div style={{ display: "flex", gap: 4, background: t.surface2, padding: 4, borderRadius: 999 }}>
          <Toggle g="week" lbl="Weekly" /><Toggle g="month" lbl="Monthly" /><Toggle g="year" lbl="Yearly" />
        </div>
      </div>

      {/* Wrongful-charge alert: the Parasite ebook should ship free with a detox/kit. */}
      {wrongful.length > 0 && (
        <Card pad={16} style={{ borderLeft: "3px solid " + t.bad, background: t.badSoft }}>
          <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
            <Icon name="alert" size={17} color={t.bad} style={{ marginTop: 1, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 13.5, color: t.fg }}>
                {wrongful.length} wrongful Parasite-ebook charge{wrongful.length > 1 ? "s" : ""} · {money(wrongfulTotal, 2)} to refund
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, marginTop: 3, lineHeight: 1.5 }}>
                Charged for the “Living Parasite Free” ebook on an order that also has a parasite detox or kit — it should have been free.
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 6, display: "flex", flexWrap: "wrap", gap: "3px 12px" }}>
                {wrongful.slice(0, 24).map((w) => (<span key={w.order + w.date}>{w.order} · {money(w.amount, 2)} · {w.date}</span>))}
                {wrongful.length > 24 ? <span>+{wrongful.length - 24} more</span> : null}
              </div>
            </div>
          </div>
        </Card>
      )}

      {empty ? (
        <Card pad={40} style={{ textAlign: "center" }}>
          <Icon name="doc" size={26} color={t.faint} />
          <div style={{ fontFamily: FIGTREE, fontSize: 14, color: t.muted, marginTop: 10 }}>No paid ebook sales yet. Titles appear here once a copy is bought.</div>
        </Card>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 16 }}>
            <Tile big={loading ? "—" : int(breakdown.bought)} color="#fff" cap={`books bought this ${perWord}`} sub="so far" />
            <Tile big={loading ? "—" : money(breakdown.revenue, 0)} color={t.good} cap={`revenue this ${perWord}`} sub="so far" />
            <Tile big={loading ? "—" : money(allRevenue, 0)} color={t.good} cap="revenue · all time" sub={int(allCopies) + " copies bought"} />
            <Tile big={loading ? "—" : int(titlesSelling)} cap="titles with sales" />
          </div>

          {/* per-book leaderboard */}
          <Card pad={0} style={{ overflow: "hidden" }}>
            <div style={{ padding: "16px 22px 12px", fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 13, color: t.fg2, borderBottom: "1px solid " + t.border }}>By title · best-seller first</div>
            <div style={{ display: "grid", gridTemplateColumns: cols, columnGap: 16, padding: "10px 22px", fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: "0.07em", fontSize: 10.5, color: t.faint, fontWeight: 700, borderBottom: "1px solid " + t.border }}>
              <div>Title</div>
              {!isMobile && <div style={{ textAlign: "right" }}>This {perWord}</div>}
              {!isMobile && <div style={{ textAlign: "right" }}>All time</div>}
              <div style={{ textAlign: "right" }}>Trend</div>
            </div>
            {loading && books.length === 0 ? (
              <div style={{ padding: 22, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading…</div>
            ) : books.map((b, i) => {
              const color = HUES[i % HUES.length];
              return (
                <div key={b.sku} style={{ display: "grid", gridTemplateColumns: cols, columnGap: 16, alignItems: "center", padding: "14px 22px", borderBottom: "1px solid " + t.border }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</div>
                      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 2 }}>
                        {isMobile ? `${int(b.bought)} bought · ${money(b.periodRev, 0)} this ${perWord} · ${int(b.allBought)} all-time ${money(b.allRev, 0)}` : `all-time ${int(b.allBought)} bought`}
                      </div>
                    </div>
                  </div>
                  {!isMobile && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 17, color: b.bought > 0 ? t.fg : t.faint }}>{int(b.bought)}<span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 11, color: t.faint }}> bought</span></div>
                      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.good }}>{money(b.periodRev, 0)}</div>
                    </div>
                  )}
                  {!isMobile && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 17, color: b.allBought > 0 ? t.fg : t.faint }}>{int(b.allBought)}<span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 11, color: t.faint }}> bought</span></div>
                      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.good }}>{money(b.allRev, 0)}</div>
                    </div>
                  )}
                  <div style={{ justifySelf: "end" }}><Spark vals={b.revSeries} color={color} /></div>
                </div>
              );
            })}
          </Card>

          {/* revenue trend by period */}
          <Card pad={22}>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 13, color: t.fg2, marginBottom: 16 }}>eBook revenue · by {perWord}</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: isMobile ? 6 : 12, height: 150 }}>
              {shown.map((p) => {
                const hPct = (p.revenue / revMax) * 100;
                return (
                  <div key={p.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, minWidth: 0 }}>
                    <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.muted, whiteSpace: "nowrap" }}>{p.revenue > 0 ? money(p.revenue, 0) : ""}</div>
                    <div style={{ width: "100%", maxWidth: 46, height: Math.max(2, hPct) + "%", minHeight: p.revenue > 0 ? 3 : 0, background: p.partial ? "repeating-linear-gradient(45deg," + t.accent + "," + t.accent + " 4px,transparent 4px,transparent 8px)" : "linear-gradient(180deg," + t.accent + ",#7a4bd0)", borderRadius: "5px 5px 0 0", transition: "height 240ms" }} />
                    <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{p.label}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 12 }}>Paid ebook revenue only. Hatched = current {perWord} in progress.</div>
          </Card>
        </>
      )}
    </div>
  );
}
